import { useEffect, useState } from 'react';
import Button from 'src/components/ui/Button';
import { ConfettiIcon } from '@phosphor-icons/react';
import RightPanelShell from 'src/components/layouts/RightPanelShell';
import { useRightPanelControl } from 'src/hooks/useRightPanelControl';
import OnboardingContent from './OnboardingContent';
import Step from 'src/components/ui/Step';
import { useLocation } from 'react-router-dom';
import useTenantNavigate from 'src/hooks/useTenantNavigate';

export default function DashboardGuide() {
  const { hideOnboarding, setContent } = useRightPanelControl();
  const navigate = useTenantNavigate();
  const location = useLocation();

  const [steps, setSteps] = useState<{
    tour: 'incomplete' | 'loading' | 'complete';
    engagement: 'incomplete' | 'loading' | 'complete';
  }>({
    tour: 'loading',
    engagement: 'incomplete'
  });
  const [topChampionName, setTopChampionName] = useState<string>('');

  // Ensure underlying page is dashboard
  useEffect(() => {
    if (!location.pathname.includes('/dashboard')) {
      navigate('/dashboard');
    }
    try {
      const saved = localStorage.getItem('innex-dashboard-top-champion-name');
      if (saved) setTopChampionName(saved);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tour completed auto-advance
  useEffect(() => {
    const completed = localStorage.getItem('innex-dashboard-tour-completed') === 'true';
    if (completed) {
      setSteps((s) => ({ ...s, tour: 'complete', engagement: 'loading' }));
    }
  }, []);

  useEffect(() => {
    if (steps.tour !== 'loading') return;
    const id = setInterval(() => {
      const done = localStorage.getItem('innex-dashboard-tour-completed') === 'true';
      if (done) {
        setSteps((s) => ({
          ...s,
          tour: 'complete',
          engagement: s.engagement === 'incomplete' ? 'loading' : s.engagement
        }));
        clearInterval(id);
      }
    }, 500);
    return () => clearInterval(id);
  }, [steps.tour]);

  // Detect when User Engagement tab is active (kept for guidance, completion happens on save)
  useEffect(() => {
    if (steps.engagement !== 'loading') return;
    const id = setInterval(() => {
      try {
        const activeTab = document.querySelector('.dashboard-tabs button[aria-selected="true"]');
        const label = activeTab?.textContent?.trim();
        // No-op: we don't auto-complete on tab change; we require an explicit name entry
        if (label === 'User Engagement') {
          // could add visual hints here in future
        }
      } catch {}
    }, 800);
    return () => clearInterval(id);
  }, [steps.engagement]);

  const backToOnboarding = () => {
    setContent(<OnboardingContent onComplete={hideOnboarding} />);
  };

  const allComplete = steps.tour === 'complete' && steps.engagement === 'complete';

  const footer = allComplete ? (
    <Button
      variant="primary"
      size="md"
      className="w-full"
      onClick={() => {
        try {
          localStorage.setItem('innex-onboarding-module-dashboard-completed', 'true');
        } catch {}
        hideOnboarding();
      }}
    >
      Mark as complete
    </Button>
  ) : (
    <div className="flex flex-col gap-2">
      <Button
        variant="outline"
        size="md"
        className="w-full"
        onClick={() => {
          setSteps((s) => {
            if (s.tour === 'loading') return { ...s, tour: 'incomplete', engagement: 'loading' };
            if (s.engagement === 'loading') return { ...s, engagement: 'incomplete' };
            return s;
          });
        }}
      >
        Skip Step
      </Button>
      <Button
        variant="outline"
        size="md"
        className="w-full"
        onClick={() => {
          navigate('/tutorials/champion-dashboard');
        }}
      >
        Watch Dashboard Tutorials
      </Button>
    </div>
  );

  const header = (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        className="text-sm text-secondary-blue hover:underline self-start"
        onClick={backToOnboarding}
      >
        ← Back to all modules
      </button>
      <h2 className="text-lg font-semibold text-content-default">Dashboard</h2>
    </div>
  );

  return (
    <RightPanelShell header={header} footer={footer}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-content-subtle">Explore performance metrics and key organisational insights.</p>

        <div className="flex flex-col gap-2">
          {(() => {
            return (
              <>
                <Step
                  title="Complete Tour"
                  state={steps.tour}
                  onTitleClick={() => {
                    try {
                      localStorage.removeItem('innex-dashboard-tour-completed');
                    } catch {}
                    setSteps((s) => ({ ...s, tour: 'loading' }));
                  }}
                >
                  <div className="flex flex-col gap-2">
                    <div className="text-sm text-content-subtle">Start our short guided tour of the Dashboard.</div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        try {
                          window.dispatchEvent(new Event('start-dashboard-tour'));
                        } catch {}
                      }}
                    >
                      Start tour
                    </Button>
                  </div>
                </Step>

                <Step
                  title="Identify Top Champion"
                  state={steps.engagement}
                  isLast={true}
                  onTitleClick={() => {
                    setSteps((s) => ({ ...s, engagement: 'loading' }));
                  }}
                >
                  {steps.engagement === 'loading' ? (
                    <div className="flex flex-col gap-2">
                      <div className="text-sm text-content-subtle">
                        Open the <strong>User Engagement</strong> tab and enter the name of your top champion in the
                        last 30 days.
                      </div>
                      <div className="flex flex-col gap-2">
                        <input
                          type="text"
                          value={topChampionName}
                          onChange={(e) => setTopChampionName(e.target.value)}
                          placeholder="Enter top champion's name"
                          className="flex-1 border border-border-default rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-300"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const name = topChampionName.trim();
                            if (!name) return;
                            try {
                              localStorage.setItem('innex-dashboard-top-champion-name', name);
                            } catch {}
                            setSteps((s) => ({ ...s, engagement: 'complete' }));
                          }}
                          disabled={!topChampionName.trim()}
                        >
                          Submit
                        </Button>
                      </div>
                    </div>
                  ) : undefined}
                </Step>

                {allComplete && (
                  <Step
                    title="Congrats, you’re all set!"
                    state="loading"
                    isLast={true}
                    icon={<ConfettiIcon size={28} className="text-secondary-blue" />}
                  >
                    <div className="text-sm text-content-subtle">
                      You’re one step closer to getting the most out of CompliMind and saving yourself hours at work!
                    </div>
                  </Step>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </RightPanelShell>
  );
}
